import { Box, Checkbox, Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { ViewStateChangeEvent } from "react-map-gl/maplibre";

import { RanaButton } from "@nens/ranalib";
import { HIDE_PRINT_CLASS } from "@nens/react/components";

import { RanaMap } from "@/lib/rana-map";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";

import { SCHEMATISATIONS_EXTENTS_ID, useSchematisationsMap } from "./hooks/use-schematisations-map";
import { SchematisationMapItem } from "./types/schematisation-map-item";

type SchematisationsMapProps = {
  schematisationItemsOnMap: SchematisationMapItem[];
  isSchematisationsLoading: boolean;
  filterMap: boolean;
  setFilterMap: (value: boolean) => void;
  onMapMoveEnd: (event: ViewStateChangeEvent) => void;
  setSelectedSchematisationId: (value: number | undefined) => void;
};

export function SchematisationsMap({
  schematisationItemsOnMap,
  isSchematisationsLoading,
  filterMap,
  setFilterMap,
  onMapMoveEnd,
  setSelectedSchematisationId,
}: SchematisationsMapProps) {
  const intl = useIntl();
  const { popup, setPopup, onMapClick, mapBounds, extentBounds, clickableLayerIds, hasItemsOnMap } =
    useSchematisationsMap({ schematisationItemsOnMap });

  return (
    <Box>
      <RanaMap
        mapId="catalogue-schematisations-map"
        bounds={mapBounds}
        isLoading={isSchematisationsLoading}
        containerProps={{ rounded: "base", borderWidth: "base", overflow: "hidden", h: "lg" }}
        mapProps={{
          interactiveLayerIds: clickableLayerIds,
          onMoveEnd: onMapMoveEnd,
          onClick: onMapClick,
        }}
      >
        <RanaMap.MapScale />

        <RanaMap.ExtentLayer id={SCHEMATISATIONS_EXTENTS_ID} bounds={extentBounds} />

        {!isSchematisationsLoading && !hasItemsOnMap ? (
          <RanaMap.Overlay color="brand.clear_creek">
            <FormattedMessage
              id="text.no_schematisations_on_map"
              defaultMessage="No model schematisations with extent are available for the map"
            />
          </RanaMap.Overlay>
        ) : null}

        {popup ? (
          <RanaPopup
            anchor="left"
            point={popup.point}
            onClose={() => setPopup(null)}
            header={<FormattedMessage id="popup.models" defaultMessage="Model schematisations" />}
          >
            <Flex direction="column" gap={2} minW={56}>
              {popup.items.map(({ modelId, modelName, schematisationName, schematisationId }) => (
                <RanaButton
                  key={modelId}
                  variant="fourthiary"
                  justifyContent="flex-start"
                  h="auto"
                  py={2}
                  onClick={() => {
                    setSelectedSchematisationId(schematisationId);
                    setPopup(null);
                  }}
                >
                  <Flex direction="column" align="flex-start">
                    <Text fontWeight={700}>{schematisationName}</Text>
                    <Text color="brand.clear_creek" textStyle="sm">
                      {modelName}
                    </Text>
                  </Flex>
                </RanaButton>
              ))}
            </Flex>
          </RanaPopup>
        ) : null}

        <RanaMap.ControlsBottom className={HIDE_PRINT_CLASS}>
          <Checkbox
            h="small_button"
            px={2}
            title={intl.formatMessage({
              id: "map.filter_schematisations",
              defaultMessage:
                "Enable this option to only show schematisations in the list below that are currently within view on the map",
            })}
            isChecked={filterMap}
            onChange={() => setFilterMap(!filterMap)}
          >
            <Text fontSize="sm">
              <FormattedMessage
                id="map.update_results"
                defaultMessage="Update model schematisation list while moving"
              />
            </Text>
          </Checkbox>
        </RanaMap.ControlsBottom>
      </RanaMap>
    </Box>
  );
}
